import {
  missionListFocusParams,
  taskListFocusParams,
} from "@/components/mission-control/missionListFocus";
import { caseCheckingListFocusParams } from "@/navigation/apps/caseCheckingLocation";
import { LIST_FOCUS_DETAIL_PARAM_KEYS, type AgentEntityDescriptor, type AgentEntityKind } from "./types";

export const AGENT_NAVIGATION_CATALOG_VERSION = 1;

const DEFAULT_STRIP_KEYS = LIST_FOCUS_DETAIL_PARAM_KEYS;

function listHighlight(
  routePath: string,
  focusPrefix: string,
  extraParams?: (id: string) => Record<string, string>,
): AgentEntityDescriptor["listHighlight"] {
  return {
    focusKey: (id) => `${focusPrefix}:${id}`,
    windowParams: (id) => ({
      focus: `${focusPrefix}:${id}`,
      ...(extraParams?.(id) ?? {}),
    }),
    routePath,
    routeQuery: (id) => ({
      focus: `${focusPrefix}:${id}`,
      ...(extraParams?.(id) ?? {}),
    }),
    stripDetailParamKeys: DEFAULT_STRIP_KEYS,
  };
}

export const AGENT_ENTITY_DESCRIPTORS: readonly AgentEntityDescriptor[] = [
  {
    kind: "mission",
    appId: "mission-control",
    listWindowTitle: "Mission Control",
    listHighlight: {
      focusKey: (id) => `mission:${id}`,
      windowParams: (id) => missionListFocusParams(`mission:${id}`),
      routePath: "/mission-control",
      routeQuery: (id) => missionListFocusParams(`mission:${id}`),
      stripDetailParamKeys: DEFAULT_STRIP_KEYS,
    },
    detail: {
      windowParamKey: "missionId",
      routePath: (id) => `/missions/${id}`,
      windowTitle: (label) => label,
      detailAppId: "mission",
    },
  },
  {
    kind: "task",
    appId: "mission-control",
    listWindowTitle: "Mission Control",
    listHighlight: {
      focusKey: (id) => `task:${id}`,
      windowParams: (id) => taskListFocusParams(`task:${id}`),
      routePath: "/mission-control",
      routeQuery: (id) => taskListFocusParams(`task:${id}`),
      stripDetailParamKeys: DEFAULT_STRIP_KEYS,
    },
    detail: {
      windowParamKey: "taskId",
      routePath: (id) => `/mission-control/tasks/${encodeURIComponent(id)}`,
      windowTitle: () => "Mission Control",
      extraWindowParams: { view: "tasks" },
    },
  },
  {
    kind: "client",
    appId: "clients",
    listWindowTitle: "Clients",
    listHighlight: listHighlight("/clients", "client"),
    detail: {
      windowParamKey: "faClientId",
      routePath: (id) => `/clients/${encodeURIComponent(id)}`,
      windowTitle: (label) => label,
      detailAppId: "client",
    },
  },
  {
    kind: "lead",
    appId: "leads",
    listWindowTitle: "Leads",
    listHighlight: listHighlight("/leads", "lead"),
    detail: {
      windowParamKey: "faClientId",
      routePath: (id) => `/leads/${encodeURIComponent(id)}`,
      windowTitle: (label) => label,
      detailAppId: "lead",
    },
  },
  {
    kind: "case_check",
    appId: "case-checking",
    listWindowTitle: "Case Checking",
    listHighlight: {
      focusKey: (id) => `case_check:${id}`,
      windowParams: (id) => caseCheckingListFocusParams(`case_check:${id}`),
      routePath: "/case-checking",
      routeQuery: (id) => caseCheckingListFocusParams(`case_check:${id}`),
      stripDetailParamKeys: DEFAULT_STRIP_KEYS,
    },
    detail: {
      windowParamKey: "caseId",
      routePath: (id) => `/case-checking/${encodeURIComponent(id)}`,
    },
  },
  {
    kind: "disclosure",
    appId: "disclosures",
    listWindowTitle: "Disclosures",
    listHighlight: listHighlight("/disclosures", "disclosure"),
    detail: {
      windowParamKey: "caseId",
      routePath: (id) => `/disclosures/${encodeURIComponent(id)}`,
    },
  },
  {
    kind: "meeting",
    appId: "meetings",
    listWindowTitle: "Meetings",
    listHighlight: listHighlight("/meetings", "meeting"),
    detail: {
      windowParamKey: "meetingId",
      routePath: (id) => `/meetings/${encodeURIComponent(id)}?view=full`,
      windowTitle: (label) => label,
      extraWindowParams: { view: "full" },
      detailAppId: "meeting",
    },
  },
  {
    kind: "artifact",
    appId: "artifact",
    listWindowTitle: "Letter",
    detail: {
      windowParamKey: "id",
      routePath: (id) => `/artifacts/${encodeURIComponent(id)}`,
    },
  },
  {
    kind: "portfolio",
    appId: "portfolio",
    listWindowTitle: "Portfolio",
    detail: {
      windowParamKey: "id",
      routePath: (id) => `/portfolio/${encodeURIComponent(id)}`,
      windowTitle: (label) => label,
    },
  },
  {
    kind: "product",
    appId: "product",
    listWindowTitle: "Fund",
    detail: {
      windowParamKey: "id",
      routePath: (id) => `/product/${encodeURIComponent(id)}`,
      windowTitle: (label) => label,
    },
  },
];

const descriptorByKind = new Map<AgentEntityKind, AgentEntityDescriptor>(
  AGENT_ENTITY_DESCRIPTORS.map((descriptor) => [descriptor.kind, descriptor]),
);

export function getAgentEntityDescriptor(kind: AgentEntityKind): AgentEntityDescriptor | undefined {
  return descriptorByKind.get(kind);
}

export function getAppIdForEntityKind(kind: AgentEntityKind): string | undefined {
  return descriptorByKind.get(kind)?.appId;
}

/** True when ``appId`` may be opened via fe_show for this profile allowlist. */
export function isFeOpenAppIdAllowed(
  appId: string,
  allowedOpenAppIds: readonly string[] | undefined,
): boolean {
  if (!allowedOpenAppIds) {
    return true;
  }
  if (allowedOpenAppIds.includes(appId)) {
    return true;
  }
  for (const descriptor of AGENT_ENTITY_DESCRIPTORS) {
    const detailAppId = descriptor.detail?.detailAppId;
    if (!detailAppId || detailAppId !== appId) {
      continue;
    }
    if (allowedOpenAppIds.includes(descriptor.appId)) {
      return true;
    }
    if (appId === "mission" && allowedOpenAppIds.includes("missions")) {
      return true;
    }
  }
  if (appId === "artifact" && allowedOpenAppIds.includes("artifacts")) {
    return true;
  }
  return false;
}

export function listAgentEntityKinds(): AgentEntityKind[] {
  return AGENT_ENTITY_DESCRIPTORS.map((descriptor) => descriptor.kind);
}

/** JSON-safe catalog for profile sync (ani_api / prompts). */
export function exportAgentNavigationCatalog() {
  return {
    version: AGENT_NAVIGATION_CATALOG_VERSION,
    entities: AGENT_ENTITY_DESCRIPTORS.map((descriptor) => ({
      kind: descriptor.kind,
      app_id: descriptor.appId,
      modes: [
        ...(descriptor.listHighlight ? ["list_highlight"] : []),
        ...(descriptor.detail ? ["detail"] : []),
      ],
    })),
  };
}
